import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Lightbulb, Zap, Bot, ChevronDown } from 'lucide-react';
import IntakeModal from '../components/IntakeModal';

const SmartStart = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [isIntakeOpen, setIsIntakeOpen] = useState(false);
  const [intakeType, setIntakeType] = useState('client-zero');

  const openIntake = (type) => {
    setIntakeType(type);
    setIsIntakeOpen(true);
  };

  // AI Discovery includes
  const discoveryIncludes = [
    "Review of current workflows, customer journeys and repetitive processes",
    "Identification of where AI and automation could create measurable value",
    "Prioritization based on business impact, feasibility and effort",
    "Automation and integration recommendations",
    "Practical AI roadmap with clear next steps"
  ];

  // Who it's for
  const idealFor = [
    "Know AI matters but aren't sure where it belongs",
    "Want expert insight before investing in implementation",
    "Are losing time or opportunities to manual processes",
    "Are ready to take action within the next 30–60 days"
  ];

  // FAQ items
  const faqItems = [
    {
      question: "How is this different from the AVA Client Zero program?",
      answer: "The AI Discovery Session is for organizations that want to identify where AI belongs before committing to implementation. Client Zero is for organizations ready to implement AVA as an AI employee against a specific workflow. Many clients start with Discovery, then move to Client Zero once they've identified the right use case."
    },
    {
      question: "What happens after the Discovery Session?",
      answer: "You'll receive a clear roadmap outlining what to automate, what to prioritize, and what can wait. From there, you can move forward with implementation through Pecaro Consulting (including AVA), use the roadmap internally, or pause with clarity — no pressure."
    },
    {
      question: "Is this only for large organizations?",
      answer: "No. This is for any organization that handles workflows, customer inquiries, or operational processes that consume employee time. If automation could save you time, improve follow-up, or prevent missed opportunities, this discovery session is likely a fit."
    },
    {
      question: "Do you work with specific tools or platforms?",
      answer: "We're platform-agnostic. Our recommendations are based on your current systems, your team's capacity, and your growth goals. We design solutions that fit your operations — not the other way around."
    },
    {
      question: "What if we're not ready to implement right away?",
      answer: "That's completely fine. The Discovery Session is designed to give you clarity first. Many clients use the roadmap to plan budgets, timelines, or internal alignment before moving forward. There's no obligation to implement immediately."
    },
    {
      question: "How does this connect to AVA and ALTAM?",
      answer: "If the Discovery Session identifies customer-facing or operational workflows that would benefit from an AI employee, we can discuss whether AVA is the right solution. AVA is one of the implementation options available through Pecaro Consulting."
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F5F5F5] via-white to-[#F5F5F5]"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#227AFF]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#227AFF]/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-[#227AFF]/10 px-4 py-2 rounded-full mb-6">
              <Lightbulb className="h-5 w-5 text-[#227AFF]" />
              <span className="text-[#227AFF] font-semibold">Start With Clarity</span>
            </div>
            
            <h1 className="mb-4">
              AI <span className="text-gradient">Discovery</span> Session
            </h1>
            <p className="text-2xl font-semibold text-gray-700 mb-6">
              Know Where AI Belongs in Your Business
            </p>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Before investing in automation or implementation, most organizations need one thing first: clarity on where AI can create measurable value.
            </p>
            <p className="text-lg text-gray-500 max-w-3xl mx-auto">
              Our AI Discovery Session gives you a clear assessment of where your business is losing time, money or opportunities — and exactly what to prioritize first.
            </p>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="mb-4">What You'll Get</h2>
              <div className="accent-line"></div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-xl bg-[#227AFF]/10">
                  <Lightbulb className="h-8 w-8 text-[#227AFF]" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">AI Discovery Session</h3>
                  <p className="text-[#227AFF] font-medium">Clarity before commitment</p>
                </div>
              </div>
              
              <p className="text-gray-600 mb-6">
                A structured assessment to identify where AI and automation could create the most value in your business.
              </p>
              
              <div className="border-t border-gray-100 pt-6">
                <p className="font-semibold text-gray-800 mb-4">Includes:</p>
                <ul className="space-y-3 mb-6">
                  {discoveryIncludes.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-[#227AFF] mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700">
                  <span className="font-semibold">Deliverable:</span> A practical roadmap you can act on immediately — whether you implement with Pecaro or take it in-house.
                </p>
              </div>
            </div>

            <div className="text-center">
              <Link to="/book-consultation" className="btn btn-primary text-lg px-8">
                Book Your Discovery Session
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Path to AVA */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-center mb-4">Your Path to AI Implementation</h2>
            <div className="accent-line"></div>
            
            <p className="text-lg text-gray-600 text-center mt-6 mb-10">
              The Discovery Session often leads to one of these next steps:
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-10">
              <div className="bg-white rounded-xl p-6 shadow-sm text-center">
                <div className="p-3 rounded-full bg-[#227AFF]/10 w-fit mx-auto mb-4">
                  <Lightbulb className="h-6 w-6 text-[#227AFF]" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">AI Strategy</h4>
                <p className="text-gray-600 text-sm">Develop a comprehensive AI roadmap for your organization</p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-sm text-center">
                <div className="p-3 rounded-full bg-[#227AFF]/10 w-fit mx-auto mb-4">
                  <Zap className="h-6 w-6 text-[#227AFF]" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">AI Automation</h4>
                <p className="text-gray-600 text-sm">Implement workflow automation and system integrations</p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-sm text-center border-2 border-[#227AFF]">
                <div className="p-3 rounded-full bg-[#227AFF]/10 w-fit mx-auto mb-4">
                  <Bot className="h-6 w-6 text-[#227AFF]" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">AVA Client Zero</h4>
                <p className="text-gray-600 text-sm">Deploy an AI employee for customer or operational workflows</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-[#0A1538] to-[#1a2d5c] rounded-xl p-8 text-center">
              <p className="text-gray-300 mb-4">Ready to deploy an AI employee?</p>
              <p className="text-white font-semibold text-xl mb-6">
                Skip straight to AVA Client Zero if you already know the workflow you want to automate.
              </p>
              <button onClick={() => openIntake('client-zero')} className="btn btn-accent inline-flex items-center gap-2" data-testid="apply-client-zero-btn">
                Apply for Client Zero <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="mb-4">Is This Right for You?</h2>
              <div className="accent-line"></div>
              <p className="text-lg text-gray-600 mt-6">The AI Discovery Session is ideal if you:</p>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-12">
              {idealFor.map((item, index) => (
                <div key={index} className="bg-white rounded-lg p-4 shadow-sm flex items-start">
                  <CheckCircle className="h-5 w-5 text-[#227AFF] mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>

            <div className="text-center">
              <Link to="/book-consultation" className="btn btn-primary text-lg px-8">
                Book a Strategy Call
              </Link>
              <p className="text-sm text-gray-600 mt-4 max-w-lg mx-auto">
                We'll determine whether the Discovery Session is right for your situation — and discuss alternatives if not.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="mb-4">Frequently Asked Questions</h2>
              <div className="accent-line"></div>
            </div>

            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
                >
                  <button
                    className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span className="font-semibold text-gray-800 pr-4">{item.question}</span>
                    <ChevronDown 
                      className={`h-5 w-5 text-[#227AFF] transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5">
                      <p className="text-gray-600">{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section section-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="mb-6 text-white">Start With the Problem</h2>
            <p className="text-xl text-gray-300 mb-8">
              Tell us where your organization is losing time, money or opportunities. We'll help you determine whether AI can create measurable value — and what to do next.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <Link to="/book-consultation" className="btn btn-accent text-lg px-8">
                Book Your AI Strategy Session
              </Link>
              <button onClick={() => openIntake('meet-ava')} className="btn btn-glass text-lg px-6 inline-flex items-center gap-2">
                Meet AVA <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            
            <p className="text-sm text-gray-400">
              There's no obligation — just clarity, strategy, and your next step forward.
            </p>
          </div>
        </div>
      </section>

      {/* Intake Modal */}
      <IntakeModal 
        isOpen={isIntakeOpen} 
        onClose={() => setIsIntakeOpen(false)} 
        type={intakeType}
      />
    </div>
  );
};

export default SmartStart;
